import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { drillsAPI, progressAPI, plansAPI, uploadAPI, scoresAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import SubscriptionGate from '../../components/SubscriptionGate';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Clock, Loader2, Play, Star } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import HLSPlayer from '../../components/HLSPlayer';
import PostSessionScore from '../../components/PostSessionScore';
import DrillTimer from '../../components/DrillTimer';
import VideoSourcePicker from '../../components/VideoSourcePicker';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fireDayCompleteConfetti() {
  const end = Date.now() + 1800;
  const colors = ['#2563EB', '#00E5FF', '#10B981', '#FF9100', '#ffffff'];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

function getScoreColor(pct) {
  if (pct >= 90) return '#10B981';
  if (pct >= 70) return '#2563EB';
  if (pct >= 50) return '#FF9100';
  return '#EF4444';
}

export default function DrillDetail() {
  const { drillId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const weekNumber = parseInt(searchParams.get('week') || '0');
  const dayNumber = parseInt(searchParams.get('day') || '0');

  const [drill, setDrill] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeVideoIdx, setActiveVideoIdx] = useState(0);
  const [dayComplete, setDayComplete] = useState(false);
  const [dayLabel, setDayLabel] = useState('');
  const [isLate, setIsLate] = useState(false);
  const [effort, setEffort] = useState(7);
  const [showEffortSlider, setShowEffortSlider] = useState(false);
  const [timerCompleted, setTimerCompleted] = useState(false);
  const [scoreSummary, setScoreSummary] = useState(null);
  const [runningScore, setRunningScore] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [drillRes, progRes, planRes] = await Promise.all([
          drillsAPI.get(drillId),
          progressAPI.weekly(weekNumber),
          plansAPI.current(),
        ]);
        // Merge per-plan-day timer override (if coach set one for this drill on this day)
        const planDay = planRes.data?.days?.find(d => d.day_number === dayNumber);
        const planDrillWithTimer = planDay?.drills?.find(d => d.id === drillId);
        const merged = { ...drillRes.data };
        if (planDrillWithTimer?.timer?.enabled) merged.timer = planDrillWithTimer.timer;
        setDrill(merged);
        const isDone = (progRes.data || []).some(
          p => p.drill_id === drillId && p.week_number === weekNumber && p.day_number === dayNumber
        );
        setCompleted(isDone);
      } catch {
        toast.error('Failed to load drill');
      }
      setLoading(false);
    };
    load();
  }, [drillId, weekNumber, dayNumber]);

  const checkDayCompletion = useCallback(async () => {
    try {
      const [planRes, progRes, summaryRes] = await Promise.all([
        plansAPI.current(),
        progressAPI.weekly(weekNumber),
        scoresAPI.summary(),
      ]);
      const plan = planRes.data;
      const day = plan?.days?.find(d => d.day_number === dayNumber);
      if (!day) return { allDone: false, nextDrillId: null };
      setDayLabel(day.day_label || day.title || `Day ${dayNumber}`);
      const dayDrills = day.drills || [];
      if (dayDrills.length === 0) return { allDone: false, nextDrillId: null };
      const completedIds = new Set(
        (progRes.data || []).filter(p => p.day_number === dayNumber).map(p => p.drill_id)
      );
      const allDone = dayDrills.every(d => completedIds.has(d.id));
      // Find next incomplete drill (in plan order, skipping the one we just finished)
      const nextDrill = dayDrills.find(d => d.id !== drillId && !completedIds.has(d.id));
      if (allDone) {
        setScoreSummary(summaryRes.data);
        setDayComplete(true);
        setTimeout(fireDayCompleteConfetti, 300);
      }
      return { allDone, nextDrillId: nextDrill?.id || null };
    } catch { return { allDone: false, nextDrillId: null }; }
  }, [dayNumber, weekNumber, drillId]);

  const handleMarkComplete = () => {
    if (completed || completing) return;
    // If drill has a timer, require it to be completed first
    if (drill?.timer?.enabled && !timerCompleted) {
      toast.error('Finish the interval timer before marking this drill complete');
      return;
    }
    setShowEffortSlider(true);
  };

  const handleComplete = async () => {
    setCompleting(true);
    setShowEffortSlider(false);
    try {
      const res = await progressAPI.complete({
        drill_id: drillId,
        week_number: weekNumber,
        day_number: dayNumber,
        effort_rating: effort,
      });
      setCompleted(true);
      const late = res.data?.progress?.is_late || false;
      setIsLate(late);

      // Show running score
      const dailyScore = res.data?.daily_score;
      if (dailyScore) {
        setRunningScore(dailyScore);
        setTimeout(() => setRunningScore(null), 4000);
      }

      toast.success(late ? 'Drill completed (late catch-up)!' : 'Drill completed!');
      const { allDone, nextDrillId } = await checkDayCompletion();
      // Auto-advance to next drill if one exists and day isn't fully done
      if (!allDone && nextDrillId) {
        setTimeout(() => {
          navigate(`/drill/${nextDrillId}?week=${weekNumber}&day=${dayNumber}`, { replace: true });
        }, 1500);
      }
    } catch {
      toast.error('Failed to mark complete');
    }
    setCompleting(false);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      await uploadAPI.video(file, (e) => {
        setUploadProgress(Math.round((e.loaded * 100) / e.total));
      });
      toast.success('Video uploaded! Submit it for coach feedback.');
      navigate('/feedback');
    } catch {
      toast.error('Upload failed');
    }
    setUploading(false);
    setUploadProgress(0);
  };

  if (loading) {
    return (
      <Layout title="DRILL" showBack>
        <div className="flex justify-center py-24"><div className="spinner" /></div>
      </Layout>
    );
  }

  if (!drill) {
    return (
      <Layout title="DRILL" showBack>
        <div className="px-4 py-12 text-center text-[#64748B]">Drill not found.</div>
      </Layout>
    );
  }

  return (
    <Layout title={drill.category?.toUpperCase()} showBack>
      <SubscriptionGate feature="training">
        <div>
          {/* Video Player(s) */}
          {(() => {
            const videos = (drill.video_filenames && drill.video_filenames.length)
              ? drill.video_filenames
              : (drill.video_filename ? [drill.video_filename] : []);
            if (videos.length === 0) {
              return (
                <div className="bg-white aspect-video w-full flex items-center justify-center">
                  <div className="text-center">
                    <Play size={48} className="text-[#1C2B54] mx-auto mb-2" />
                    <p className="text-[#64748B] text-sm">No video attached</p>
                  </div>
                </div>
              );
            }
            return (
              <>
                <div className="bg-black aspect-video w-full" data-testid="drill-video-active">
                  <HLSPlayer
                    key={videos[activeVideoIdx] || videos[0]}
                    videoFilename={videos[activeVideoIdx] || videos[0]}
                    watermarkText={user?.name || user?.email}
                    data-testid="drill-video"
                  />
                </div>
                {videos.length > 1 && (
                  <div className="px-4 pt-3" data-testid="drill-video-tabs">
                    <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                      {videos.map((fn, i) => (
                        <button
                          key={fn + i}
                          type="button"
                          onClick={() => setActiveVideoIdx(i)}
                          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors ${
                            i === activeVideoIdx
                              ? 'bg-[#2563EB] text-white'
                              : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]'
                          }`}
                          data-testid={`drill-video-tab-${i}`}
                        >
                          <Play size={11} fill={i === activeVideoIdx ? 'currentColor' : 'none'} />
                          Clip {i + 1}
                        </button>
                      ))}
                      <span className="text-[10px] text-[#94A3B8] ml-1 whitespace-nowrap">{videos.length} clips</span>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* Timer (coach-enabled per plan-day) — directly under the video */}
          {drill.timer?.enabled && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="px-4 pt-4"
            >
              <DrillTimer
                timer={drill.timer}
                onComplete={() => setTimerCompleted(true)}
              />
            </motion.div>
          )}

          <div className="px-4 py-6 space-y-6">
            {/* Title & Meta */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] text-[#00E5FF] font-bold uppercase tracking-widest bg-[#2563EB]/10 px-2 py-0.5 rounded-full border border-[#2563EB]/30">
                  {drill.category}
                </span>
                <span className="text-[10px] text-[#64748B] font-bold uppercase tracking-wider">
                  {drill.levels && drill.levels.length === 4 ? 'All Levels' : (drill.levels || []).join(', ')}
                </span>
              </div>
              <h1 className="font-['Barlow_Condensed'] text-2xl font-bold tracking-tight" data-testid="drill-title">
                {drill.title}
              </h1>
              <div className="flex items-center gap-2 mt-2 text-[#64748B] text-sm">
                <Clock size={14} />
                <span>{drill.duration_minutes} min</span>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <p className="text-[#64748B] text-sm leading-relaxed">{drill.description}</p>
            </motion.div>

            {/* Coaching Cues */}
            {drill.coaching_cues?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight mb-3">COACHING CUES</h3>
                <div className="space-y-2">
                  {drill.coaching_cues.map((cue, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <span className="w-6 h-6 rounded-full bg-[#2563EB]/15 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-[#2563EB]">
                        {i + 1}
                      </span>
                      <p className="text-sm text-[#64748B] pt-0.5">{cue}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Mark Complete / Effort Rating */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="space-y-3">
              {completed ? (
                <div className={`${isLate ? 'bg-[#FF9100]/10 border-[#FF9100]/30' : 'bg-[#10B981]/10 border-[#10B981]/30'} border rounded-xl p-4 flex items-center gap-3`} data-testid="drill-completed">
                  <CheckCircle2 size={24} className={isLate ? "text-[#FF9100]" : "text-[#10B981]"} />
                  <div>
                    <span className={`font-['Barlow_Condensed'] text-base font-bold ${isLate ? 'text-[#FF9100]' : 'text-[#10B981]'}`}>
                      {isLate ? 'COMPLETED (LATE)' : 'COMPLETED'}
                    </span>
                    <p className="text-xs text-[#64748B]">{isLate ? 'Better late than never. Keep going.' : 'Nice work. Keep the streak alive.'}</p>
                  </div>
                </div>
              ) : showEffortSlider ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-[#2563EB]/30 rounded-xl p-5 space-y-4"
                  data-testid="effort-rating-panel"
                >
                  <div className="text-center">
                    <h4 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight">RATE YOUR EFFORT</h4>
                    <p className="text-xs text-[#64748B] mt-1">How hard did you go? Be honest.</p>
                    <p className="text-[10px] text-[#FF9100] mt-1 font-medium italic">Be honest—this impacts your development. Would your coach agree?</p>
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-xs text-[#64748B] w-8 text-right">Easy</span>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={effort}
                      onChange={e => setEffort(parseInt(e.target.value))}
                      className="flex-1 accent-[#2563EB] h-2"
                      data-testid="effort-slider"
                    />
                    <span className="text-xs text-[#64748B] w-8">Max</span>
                  </div>
                  <div className="text-center">
                    <span className="font-['Barlow_Condensed'] text-4xl font-black text-[#2563EB]" data-testid="effort-value">{effort}</span>
                    <span className="text-sm text-[#64748B]">/10</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowEffortSlider(false)}
                      className="flex-1 py-3 rounded-xl border border-[#E2E8F0] text-[#64748B] text-sm font-bold"
                      data-testid="effort-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleComplete}
                      disabled={completing}
                      className="flex-1 bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-xl py-3 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                      data-testid="effort-submit"
                    >
                      {completing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                      {completing ? 'Saving...' : 'Submit'}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <button
                  onClick={handleMarkComplete}
                  disabled={completing || (drill?.timer?.enabled && !timerCompleted)}
                  className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="mark-complete-btn"
                >
                  {completing ? <Loader2 size={18} className="animate-spin" /> :
                   (drill?.timer?.enabled && !timerCompleted) ? null :
                   <CheckCircle2 size={18} />}
                  {completing ? 'Saving...' :
                   (drill?.timer?.enabled && !timerCompleted) ? 'Finish Timer To Complete' :
                   'Mark Complete'}
                </button>
              )}

              {/* Upload Rep */}
              <div data-testid="upload-rep-section">
                {uploading ? (
                  <div className="w-full bg-transparent border-2 border-[#2563EB] text-[#0A2242] font-bold uppercase tracking-wide rounded-lg px-6 py-3 text-sm flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Uploading {uploadProgress}%
                  </div>
                ) : (
                  <>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] text-center mb-2">Upload Your Rep</p>
                    <VideoSourcePicker onFileSelected={handleUpload} disabled={uploading} compact />
                  </>
                )}
              </div>
            </motion.div>
          </div>

          {/* Running Score Toast */}
          <AnimatePresence>
            {runningScore && (
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.9 }}
                className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 bg-white border border-[#2563EB]/40 rounded-2xl px-5 py-3 shadow-2xl flex items-center gap-3"
                data-testid="running-score-toast"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${getScoreColor(runningScore.percentage)}20` }}>
                  <Star size={20} style={{ color: getScoreColor(runningScore.percentage) }} />
                </div>
                <div>
                  <p className="font-['Barlow_Condensed'] text-lg font-black" style={{ color: getScoreColor(runningScore.percentage) }}>
                    {runningScore.percentage}/100
                  </p>
                  <p className="text-[9px] text-[#64748B] uppercase tracking-widest">
                    {runningScore.drills_done}/{runningScore.drills_total} drills
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Day Complete — Post Session Score Screen */}
          <PostSessionScore
            open={dayComplete}
            scoreSummary={scoreSummary}
            dayLabel={dayLabel}
            onContinue={() => navigate(`/day/${dayNumber}?week=${weekNumber}`, { replace: true })}
          />
        </div>
      </SubscriptionGate>
    </Layout>
  );
}
